import { autoLoanFlow } from "./flows/autoLoanFlow";
import { creditCardFlow } from "./flows/creditCardFlow";
import { personalLoanFlow } from "./flows/personalLoanFlow";
import type { FlowType, ProductType } from "./types";
import type { IncomeTypeEnum, StateEnum } from "../types/api";

export type { FlowType, ProductType };

export interface FlowStep {
  id: string;
  path?: string;
  getPath?: (params: { applicationUuid?: string }) => string;
  getMatchPaths?: (params: { applicationUuid?: string }) => string[];
  condition?: (context: NavigationContext) => boolean;
}

export interface NavigationContext {
  applicationUuid?: string;
  accountApplicationUuid?: string | null;
  isPlaidLayerEnabled?: boolean;
  skippedPlaidPrefill?: boolean;
  applicationState?: StateEnum;
  isCoApplicantEnabled?: boolean;
  showLivingArrangementStep?: boolean;
  showEmploymentDetailsStep?: boolean;
  // Portal prefill: when a sidebar-launched application already carries these details
  // (copied from the applicant's most recent prior application), the OLB flow skips the
  // corresponding step. Default false (undefined) — never skip on incomplete data.
  hasPrefilledLivingArrangement?: boolean;
  hasPrefilledIncome?: boolean;
  hasPrefilledEmployment?: boolean;
  hasCoApplicant?: boolean;
  hasSeparateCoApplicantAddress?: boolean;
  hasMultipleCreditCardProducts?: boolean;
  hasSelectedCreditCardProduct?: boolean;
  hasVehicleSelected?: boolean;
  isRefinance?: boolean;
  isAutoRequestedAmountEnabled?: boolean;
  hasEligibilityCriteria?: boolean;
  hasLendingQuestions?: boolean;
  incomeType?: IncomeTypeEnum;
  coApplicantIncomeType?: IncomeTypeEnum;
}

type FlowConfiguration = Record<ProductType, Record<FlowType, FlowStep[]>>;

function resolveStepPath(step: FlowStep, applicationUuid?: string): string {
  if (step.getPath) {
    return step.getPath({ applicationUuid });
  }

  if (!step.path) {
    throw new Error(`Flow step ${step.id} is missing a path definition`);
  }

  if (!applicationUuid) {
    return step.path;
  }

  return step.path.replace(":applicationUuid", applicationUuid);
}

export { resolveStepPath };

function getStepMatchPaths(step: FlowStep, applicationUuid?: string): string[] {
  if (step.getMatchPaths) {
    return step.getMatchPaths({ applicationUuid });
  }

  return [resolveStepPath(step, applicationUuid)];
}

const UUID_PATH_PATTERN =
  /\/[^/]+\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\//i;

function getCurrentStepId(
  currentPath: string,
  steps: FlowStep[],
  applicationUuid?: string
): string | null {
  // First pass: match with provided applicationUuid
  for (const step of steps) {
    const matchPaths = getStepMatchPaths(step, applicationUuid);
    if (matchPaths.includes(currentPath)) {
      return step.id;
    }
  }

  // Second pass: match without UUID (catches non-UUID paths)
  for (const step of steps) {
    const matchPaths = getStepMatchPaths(step, undefined);
    if (matchPaths.includes(currentPath)) {
      return step.id;
    }
  }

  // Third pass: extract UUID from the current path and match with it.
  // This handles the case where the URL contains a different UUID than the
  // one being navigated to (e.g. after creating a new application while on
  // a page scoped to an old application UUID).
  const uuidMatch = UUID_PATH_PATTERN.exec(currentPath);
  if (uuidMatch && uuidMatch[1] !== applicationUuid) {
    const pathUuid = uuidMatch[1];
    for (const step of steps) {
      const matchPaths = getStepMatchPaths(step, pathUuid);
      if (matchPaths.includes(currentPath)) {
        return step.id;
      }
    }
  }

  return null;
}

export function getCurrentFlowStepId(
  currentPath: string,
  productType: ProductType,
  flowType: FlowType,
  applicationUuid?: string
): string | null {
  return getCurrentStepId(currentPath, flowConfig[productType][flowType], applicationUuid);
}

export const flowConfig: FlowConfiguration = {
  "personal-loan": personalLoanFlow,
  "credit-card": creditCardFlow,
  "auto-loan": autoLoanFlow,
};

/**
 * Get the next step in the flow based on current path and context.
 */
export function getNextStep(
  currentPath: string,
  productType: ProductType,
  flowType: FlowType,
  context: NavigationContext = {},
  applicationUuid?: string
): string | null {
  const steps = flowConfig[productType][flowType];
  const currentStepId = getCurrentStepId(currentPath, steps, applicationUuid);

  if (!currentStepId) {
    console.warn(`getNextStep: could not find current step for path "${currentPath}"`);
    return null;
  }

  const currentIndex = steps.findIndex((step) => step.id === currentStepId);

  // Find the next valid step based on conditions
  for (let i = currentIndex + 1; i < steps.length; i++) {
    const step = steps[i];
    if (!step.condition || step.condition(context)) {
      return resolveStepPath(step, applicationUuid);
    }
  }

  return null;
}

/**
 * Get the previous step in the flow based on current path and context.
 */
export function getPreviousStep(
  currentPath: string,
  productType: ProductType,
  flowType: FlowType,
  context: NavigationContext = {},
  applicationUuid?: string
): string | null {
  const steps = flowConfig[productType][flowType];
  const currentStepId = getCurrentStepId(currentPath, steps, applicationUuid);
  const currentIndex = steps.findIndex((step) => step.id === currentStepId);

  if (currentIndex === -1 || currentIndex === 0) {
    return null;
  }

  // Find the previous valid step based on conditions
  for (let i = currentIndex - 1; i >= 0; i--) {
    const step = steps[i];
    if (!step.condition || step.condition(context)) {
      return resolveStepPath(step, applicationUuid);
    }
  }

  return null;
}

/**
 * Get the ordered steps for a flow, filtered by context.
 */
export function getFlowSteps(
  productType: ProductType,
  flowType: FlowType,
  context: NavigationContext = {},
  applicationUuid?: string
): FlowStep[] {
  return flowConfig[productType][flowType]
    .filter((step) => !step.condition || step.condition(context))
    .map((step) => ({
      ...step,
      path: resolveStepPath(step, applicationUuid),
    }));
}

export function getStepPath(
  stepId: string,
  productType: ProductType,
  flowType: FlowType,
  context: NavigationContext = {},
  applicationUuid?: string
): string | null {
  const step = flowConfig[productType][flowType].find(
    (candidate) => candidate.id === stepId && (!candidate.condition || candidate.condition(context))
  );

  return step ? resolveStepPath(step, applicationUuid) : null;
}
